import { useContext, useEffect, useState } from "react";
import { TiInfo } from "react-icons/ti";
import { LabelConstants } from "../../Constants.ts";
import SearchTextbox from "../../UIComponents/SearchTextbox.tsx";
import { useAppContext } from "../../context/AppContext.tsx";
import { ThemeContext } from "../../context/ThemeContext.js";
import { RoleGroup } from "./_core/_model";

interface RoleGroupsListProps {
    roleGroupIDs: string[] | null;
    onChange: (selected: RoleGroup[]) => void,
    onInit: (groups: RoleGroup[]) => void
}

export function RoleGroupsList({ roleGroupIDs, onChange, onInit }: RoleGroupsListProps) {
    const [groups, setGroups] = useState<RoleGroup[] | null>(null);
    const [selected, setSelected] = useState<number[]>([]);
    const { allRoleGroups } = useAppContext();
    const { themeConfig } = useContext(ThemeContext);

    const [filterText, setFilterText] = useState<string>("");

    // ... existing useEffects

    const filteredGroups = (groups == null) ? [] : groups.filter(group =>
        group.roleGroupName.toLowerCase().includes(filterText.toLowerCase()) ||
        group.roleGroupDescription.toLowerCase().includes(filterText.toLowerCase())
    );

    useEffect(() => {
        if (roleGroupIDs != null)
            setSelected(roleGroupIDs.map((x) => parseInt(x)) || []);
        // if (groups.length > 0) {
        //     passInits(groups);
        // }
    }, [roleGroupIDs]);

    const passInits = (grps: RoleGroup[]) => {
        if (roleGroupIDs != null) {
            let g = grps.filter((g) => roleGroupIDs.indexOf(g.roleGroupID.toString()) > -1);
            onInit(g);
        }
    }

    useEffect(() => {
        setGroups(allRoleGroups || []);
        passInits(allRoleGroups);
    }, []);

    const toggleSelection = (id: number) => {
        if (groups != null) {
            const updated = selected.includes(id)
                ? selected.filter((x) => x !== id)
                : [...selected, id];
            setSelected(updated);
            onChange(groups.filter((g) => updated.indexOf(g.roleGroupID) > -1));
        }
    };

    const toggleSelectAll = () => {
        if (groups != null) {
            const allIDs = groups.map((g) => g.roleGroupID);
            const updated = selected.length === allIDs.length ? [] : allIDs;
            setSelected(updated);
            onChange(groups.filter((g) => updated.indexOf(g.roleGroupID) > -1));
        }
    };

    const isAllSelected = (groups == null) ? false : groups.length > 0 && selected.length === groups.length;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <SearchTextbox {...{
                    onChange: (e) => { setFilterText(e.target.value) },
                    placeholder: "Filter by name or description",
                    value: filterText
                }} />
            </div>
            <div>
                <span className="text-muted d-flex align-items-center gap-1 small">
                    <TiInfo size={16} />
                    Security Groups allow you to manage multiple roles as a single unit.
                    Add this user to a group to assign its roles automatically.
                </span>
            </div>
            <div className="table-responsive border mt-1">
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: "fixed" }}>
                    <thead className="">
                        <tr>
                            <th style={{
                                width: "50px",
                                verticalAlign: 'middle',
                                fontSize: "12px",
                                padding: '12px 4px 12px 12px',
                            }}>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isAllSelected}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th className='text-color'
                                style={{
                                    fontWeight: "normal",
                                    fontSize: "12px",
                                    padding: '12px 4px',
                                    textAlign: 'left',
                                    verticalAlign: 'middle'
                                }}>{LabelConstants.RoleGroups} Name</th>
                            <th className='text-color'
                                style={{
                                    fontWeight: "normal",
                                    fontSize: "12px",
                                    padding: '12px 4px',
                                    textAlign: 'left',
                                    verticalAlign: 'middle',
                                    width: "400px"
                                }}>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            groups == null ?
                                [...Array(5)].map((_, i) => (
                                    <tr key={i}>
                                        <td style={{ textAlign: "center" }}>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-8"></span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-8"></span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-8"></span>
                                            </div>
                                        </td>
                                    </tr>
                                )) :
                                filteredGroups.length == 0 ?
                                    <tr>
                                        <td></td>
                                        <td>No group to show</td>
                                        <td></td>
                                    </tr> :
                                    filteredGroups.map((group, inndx) => (
                                        <tr key={group.roleGroupID} style={{ backgroundColor: (inndx % 2 === 0) ? themeConfig.colors.lightGray : themeConfig.colors.card }}>
                                            <td style={{
                                                padding: '12px 4px 12px 12px',
                                                color: themeConfig.colors.text,
                                                verticalAlign: 'middle'
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selected.includes(group.roleGroupID)}
                                                    onChange={() => toggleSelection(group.roleGroupID)}
                                                />
                                            </td>
                                            <td style={{
                                                padding: '12px 4px',
                                                color: themeConfig.colors.text,
                                                verticalAlign: 'middle'
                                            }}>{group.roleGroupName}</td>
                                            <td style={{
                                                padding: '12px 4px',
                                                color: themeConfig.colors.text,
                                                verticalAlign: 'middle'
                                            }}>{group.roleGroupDescription}</td>
                                        </tr>
                                    ))
                        }
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default RoleGroupsList;
